import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Chip, IconButton, Tooltip } from '@material-ui/core';
import { Add, Delete, Edit, Visibility } from '@material-ui/icons';
import { green, grey } from '@material-ui/core/colors';

import { CustomTableTitle } from '../../../shared/components';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { enqueueSnackbar, Toast, ToastError, ToastSuccess } from '../../../shared/components/Notifier';
import { hasAddPermission, hasDeletePermission, hasUpdatePermission } from '../../../shared/lib/PermissionManager';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core';
import { DataTable, ColumnDef } from '../../components/DataTable';
import { deleteExpiringStockPromoTemplate, getExpiringStockPromoTemplates } from '../../api/Api';
import { ExpiringStockPromoTemplate } from '../../models';
import PromoTemplateFormDialog from './PromoTemplateFormDialog';

const PERMISSION_MODEL = 'expiringstockpromotemplate';

interface Props {
    userData: any;
    history: any;
    showToast: (toast: Toast) => void;
}

const ExpiringStockPromoTemplateListScreen: React.FC<Props> = (props) => {
    const { userData, history } = props;
    const { userData: authUser } = userData.userData;
    const isSuperUser = authUser?.is_superuser;
    const userPermissions = authUser?.user_permissions || [];
    const selectedBusiness = userData?.selectedBusiness;
    const businessId = selectedBusiness?.business_id ?? selectedBusiness?.id;

    const [templates, setTemplates] = useState<ExpiringStockPromoTemplate[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);
    const [formDialogState, setFormDialogState] = useState<{ open: boolean; mode: 'CREATE' | 'EDIT' | 'PREVIEW'; template: ExpiringStockPromoTemplate | null }>({
        open: false,
        mode: 'CREATE',
        template: null,
    });

    const loadTemplates = useCallback(() => {
        if (!businessId) {
            setTemplates([]);
            return;
        }
        setLoading(true);
        getExpiringStockPromoTemplates(businessId)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => setTemplates(data?.results || []))
            .catch(() => props.showToast({ message: 'Failed to load promo templates.', options: ToastError }))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, refreshToken]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const refresh = () => setRefreshToken((t) => t + 1);

    const handleDelete = (template: ExpiringStockPromoTemplate) => {
        if (!window.confirm(`Delete promo template "${template.name}"? This cannot be undone.`)) {
            return;
        }
        deleteExpiringStockPromoTemplate(template.id)
            .catch(handleError)
            .then(checkStatus)
            .then(() => {
                props.showToast({ message: 'Promo template deleted.', options: ToastSuccess });
                refresh();
            })
            .catch((error: any) => {
                const message = error?.response?.data?.error || 'Failed to delete promo template. It may be in use.';
                props.showToast({ message, options: ToastError });
            });
    };

    const canCreate = hasAddPermission(isSuperUser, userPermissions, PERMISSION_MODEL);
    const canEdit = hasUpdatePermission(isSuperUser, userPermissions, PERMISSION_MODEL);
    const canDelete = hasDeletePermission(isSuperUser, userPermissions, PERMISSION_MODEL);

    const columns: ColumnDef<ExpiringStockPromoTemplate>[] = [
        { id: 'name', header: 'Name', width: 220, render: (row) => row.name },
        { id: 'promo_type', header: 'Promo Type', width: 180, render: (row) => row.promo_type },
        { id: 'benefit_type', header: 'Benefit Type', width: 200, render: (row) => row.benefit_type },
        {
            id: 'is_default',
            header: 'Default',
            width: 100,
            align: 'center',
            render: (row) =>
                row.is_default ? (
                    <Chip label="Default" size="small" style={{ backgroundColor: green[600], color: '#fff' }} />
                ) : (
                    <span style={{ color: grey[500] }}>—</span>
                ),
        },
        {
            id: 'created_at',
            header: 'Created',
            width: 160,
            render: (row) => moment(row.created_at).format('MM/DD/YYYY HH:mm'),
        },
        {
            id: 'actions',
            header: 'Actions',
            width: 150,
            flex: 0,
            align: 'center',
            render: (row) => (
                <>
                    <Tooltip title="Preview">
                        <IconButton
                            size="small"
                            onClick={() => setFormDialogState({ open: true, mode: 'PREVIEW', template: row })}
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {canEdit && (
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                onClick={() => setFormDialogState({ open: true, mode: 'EDIT', template: row })}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canDelete && (
                        <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(row)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </>
            ),
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <CustomTableTitle history={history} title="Expiring Stock Promo Templates" icon={SVG_ICONS.addTag} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                {canCreate && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={() => setFormDialogState({ open: true, mode: 'CREATE', template: null })}
                        disabled={!businessId}
                    >
                        Add Template
                    </Button>
                )}
            </div>
            <DataTable
                columns={columns}
                rows={templates}
                getRowId={(row) => row.id}
                loading={isLoading}
                page={0}
                pageSize={Math.max(templates.length, 10)}
                totalCount={templates.length}
                onPageChange={() => undefined}
                onPageSizeChange={() => undefined}
                emptyMessage="No promo templates configured for this business yet."
            />
            {formDialogState.open && (
                <PromoTemplateFormDialog
                    open={formDialogState.open}
                    mode={formDialogState.mode}
                    template={formDialogState.template}
                    businessId={businessId}
                    onClose={() => setFormDialogState({ open: false, mode: 'CREATE', template: null })}
                    onSaved={() => {
                        setFormDialogState({ open: false, mode: 'CREATE', template: null });
                        refresh();
                    }}
                    showToast={props.showToast}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.userLocalData,
});

const mapDispatchToProps = (dispatch: any) => ({
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpiringStockPromoTemplateListScreen);
